import { Injectable } from '@angular/core';

import { WidgetDataSource } from 'app/models/widget-data-source.model';

import { OperatorType } from 'app/modules/collection/services/filter.service';

import { DataSourceItem } from 'app/modules/widget/components/data-source-chooser/data-source-chooser.component';
import {
  WidgetAnalysisDataSource,
  WidgetAnalysisGroupDataSource,
} from 'app/modules/widget/components/widget-data-source.component';
import { DataSourceFilter, DataSourceFilterSet } from 'app/providers/bridge-api/dashboard.service';
import { ObjectLabel } from 'app/providers/storage-api/object-label.service';
import { DateRangeService } from 'app/services/date-range.service';

import { DatasourcesType } from '../stores/datasource-manager.store';

@Injectable({
  providedIn: 'root',
})
export class DashboardDatasourceFilterService {
  constructor(private dateRangeService: DateRangeService) {}

  private readonly andOrHandler: {
    [type: string]: (ds: WidgetDataSource, filter: DataSourceFilter) => boolean;
  } = {
    contentChannel: (ds, filter) => ds.contentChannel === filter.value,
    dataSourceType: (ds, filter) =>
      filter.id === 'keyDriverAnalysis'
        ? ds.type === 'analysis' && ds.isDriverAnalysis
        : ds.type === filter.value,
    specificDataSource: (ds, filter) =>
      filter.type === 'specificDataSource' && filter.dataSourceType === 'group'
        ? ds.groupId === filter.value
        : ds.analysisId === filter.value,
    date: (ds, filter) => {
      return this.dateIsInRange(ds, filter);
    },
    label: (ds, filter) => (ds.objectLabels as ObjectLabel[]).some((ol) => ol.id === filter.id),
  };

  private readonly filterHandlers: {
    [op in OperatorType]?: {
      [type: string]: (
        ds:
          | ((WidgetAnalysisDataSource | WidgetAnalysisGroupDataSource) & { key: string })
          | WidgetDataSource,
        filter: DataSourceFilter
      ) => boolean;
    };
  } = {
    and: this.andOrHandler,
    or: this.andOrHandler,
    not: {
      contentChannel: (ds, filter) => ds.contentChannel !== filter.value,
      dataSourceType: (ds, filter) =>
        filter.id === 'keyDriverAnalysis'
          ? !(ds.type === 'analysis' && ds.isDriverAnalysis)
          : ds.type !== filter.value,
      specificDataSource: (ds, filter) =>
        filter.type === 'specificDataSource' && filter.dataSourceType === 'group'
          ? ds.groupId !== filter.value
          : ds.analysisId !== filter.value,

      date: (ds, filter) => {
        return !this.dateIsInRange(ds, filter);
      },
      label: (ds, filter) => (ds.objectLabels as ObjectLabel[]).every((ol) => ol.id !== filter.id),
    },
  };

  private applyFilters<T>(
    items: T[],
    filters: DataSourceFilterSet,
    getId: (item: T) => string,
    getValue: (
      item: T
    ) =>
      | ((WidgetAnalysisDataSource | WidgetAnalysisGroupDataSource) & { key: string })
      | WidgetDataSource
  ): T[] {
    for (const op of ['and', 'not'] as const) {
      for (const filter of filters[op] ?? []) {
        const handler = this.filterHandlers[op]?.[filter.type];
        if (handler) {
          items = items.filter((item) => handler(getValue(item), filter));
        }
      }
    }

    if (filters.or?.length) {
      const orMatches = filters.or.flatMap((filter) => {
        const handler = this.filterHandlers.or?.[filter.type];
        return handler ? items.filter((item) => handler(getValue(item), filter)) : [];
      });

      // Remove duplicates
      const seen = new Set<string>();
      items = orMatches.filter((item) => {
        const id = getId(item);
        if (seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      });
    }

    return items;
  }

  filterDataSourceItems(
    filters: DataSourceFilterSet = { and: [], or: [], not: [] },
    dataSourceItems: DataSourceItem[]
  ) {
    return this.applyFilters(
      dataSourceItems,
      filters,
      (ds) => ds.value.analysisId ?? ds.value.groupId ?? ds.value.id,
      (ds) => ds.value
    );
  }

  filterDataSourcesMap(
    dataSources: DatasourcesType,
    filters: DataSourceFilterSet = { and: [], or: [], not: [] }
  ) {
    const reverseDataSources = new WeakMap<WidgetDataSource, string>(
      Object.entries(dataSources).flatMap(([key, arr]) => arr.map((item) => [item, key] as const))
    );

    const filtered = this.applyFilters(
      Object.values(dataSources).flatMap((arr) => arr),
      filters,
      (ds) => ds.analysisId ?? ds.groupId ?? ds.id,
      (ds) => ds
    );

    return filtered.reduce((acc, item) => {
      (acc[reverseDataSources.get(item)] ||= []).push(item);
      return acc;
    }, {});
  }

  private dateIsInRange(ds: WidgetDataSource, filter: DataSourceFilter) {
    if (filter.type !== 'date') {
      return;
    }
    const targetVal = filter.dateKind === 'modified' ? ds.modifiedAt : ds.createdAt;
    if (!targetVal) {
      return false;
    }
    const target = new Date(targetVal);
    let start: Date;
    let end: Date;
    if (filter.value.startsWith('daterange')) {
      const fixedRange = filter.value.split('/');
      start = new Date(fixedRange[1]);
      end = new Date(fixedRange[2]);
    } else {
      [start, end] = this.dateRangeService
        .computeRelativeFixedValueForDateFilter(filter.value)
        .map((date) => new Date(date));
    }

    return target.getTime() >= start.getTime() && target.getTime() <= end.getTime();
  }

  getFilterCounts(dataSourceItems: DataSourceItem[], objectLabels: DataSourceFilter[]) {
    // Use a flexible structure so any contentChannel is supported
    const counts: any = {
      analysis: { count: 0, dsMatches: [] },
      keyDriverAnalysis: { count: 0, dsMatches: [] },
      group: { count: 0, dsMatches: [] },
      label: {},
    };
    for (const ol of objectLabels) {
      counts.label[ol.id] = { id: ol.id, count: 0, dsMatches: [] };
    }
    for (const dsItem of dataSourceItems) {
      const ch = dsItem.value.contentChannel;
      if (ch) {
        counts[ch] = counts[ch] || { count: 0, dsMatches: [] };
        counts[ch].count += 1;
        counts[ch].dsMatches.push(`\n${dsItem.display}`);
      }
      if (dsItem.value.type === 'analysis') {
        counts.analysis.count += 1;
        counts.analysis.dsMatches.push(`\n${dsItem.display}`);
        if (dsItem.value.isDriverAnalysis) {
          counts.keyDriverAnalysis.count += 1;
          counts.keyDriverAnalysis.dsMatches.push(`\n${dsItem.display}`);
        }
      }
      if (dsItem.value.type === 'group') {
        counts.group.count += 1;
        counts.group.dsMatches.push(`\n${dsItem.display}`);
      }

      const dsLabels = dsItem.value.objectLabels || [];
      for (const dsLabel of dsLabels as ObjectLabel[]) {
        if (counts.label[dsLabel.id]) {
          counts.label[dsLabel.id].count += 1;
          counts.label[dsLabel.id].dsMatches.push(`\n${dsItem.display}`);
        }
      }
    }
    return counts;
  }

  getIsActiveDataSourceFilteredOut(dataSource: WidgetDataSource, dataSources: DatasourcesType) {
    const id = dataSource.analysisId ?? dataSource.groupId ?? dataSource.id;
    return !Object.values(dataSources)
      .reduce((prev, curr) => prev.concat(curr), [])
      .some((ds) => {
        const dsId = ds.analysisId ?? ds.groupId ?? ds.id;
        return dsId === id;
      });
  }
}
